import environmentVariables from '../../appVariables';
import { FeaturesEnum } from '../../constants/enums/FeaturesEnum';
import { VisualFeatureKey } from '../../constants/enums/VisualFeatureKey';
import { IHideCompleteButtonConfig } from '../../hooks/useHideCompleteButton/useHideCompleteButton';
import { IFeatureWithValue } from '../../operations/query/public/features/useGetFeaturesWithValues';
import { normalizeVisualFeatures } from '../../utils/featureFlags';
import { toCamelCaseFromSnakeCase } from '../../utils/formatString';
import { ICustomRedirectUrl } from '../../utils/getCustomRedirectUrl';
import { ILPCoursesNumberProviders } from '../../utils/hideCoursesNumberUtils';
import { IDefaultLanguage } from '../../utils/languageUtils';

export interface IDefaultStudentInstitution {
  id: string;
}

export interface ISsoProviderId {
  id: string;
}

export interface IDefaultAcademicProgram {
  id: string;
}

export interface IDefaultCountry {
  country: string;
}

export interface IEmbededLogin {
  url: string;
}

export interface IQsemLearnerDefaultLevelOfStudy {
  type: string;
}

export interface IQsemShowSidebarButton {
  text: string;
  link: string;
}

export interface IQsemHomepageByStudentType {
  type: string;
  route: string;
}

export interface IQsemDesignCustomLogin {
  descriptionText?: {
    [key: string]: string;
  };
  loginLogo: string;
  buttonText: {
    [key: string]: string;
  };
  buttonIcon?: string;
  visibilityRedirect?: boolean;
  loginMethod: string;
}

export interface IQsemContactInfoPlar {
  descriptionText: {
    [key: string]: string;
  };
  contactText: {
    [key: string]: string;
  };
}

export interface IQsemCoursesFilterByPrice {
  price: string;
}
export interface IFeaturedPrograms {
  titleEn: string;
  titleEs: string;
  subTitleEn: string;
  subTitleEs: string;
  learningPaths: string[];
}
export interface IQsemPlarFeaturedPrograms {
  featuredPrograms: IFeaturedPrograms;
}

export interface IQsemShowChatBot {
  url: string;
  institutionName?: string;
  position?: 'bottom-left' | 'bottom-right';
  offsetX?: string;
  offsetY?: string;
}

export interface IAcademicProgramLpMappingEntry {
  academicProgramId: string;
  learningPathId: string;
}

export interface IAcademicProgramLpMapping {
  defaultAcademicProgram?: string;
  mappings: IAcademicProgramLpMappingEntry[];
}

export interface IFeatures {
  googleSSOEnabled?: boolean;
  learner?: {
    qsemLearnerDefaultStudentInstitution?: IDefaultStudentInstitution;
    qsemLearnerDefaultLevelOfStudy?: IQsemLearnerDefaultLevelOfStudy;
    qsemEnablePlar: boolean;
    qsemLearnerMarketInsightsInterest?: boolean;
    qsemAcademicProgramLpMapping?: IAcademicProgramLpMapping;
  };
  qsemLangDefaultLanguage?: IDefaultLanguage;
  defaultCountry?: IDefaultCountry;
  occupationsLimitedEnabled?: boolean;
  qsemEnableAuthSaml: boolean;
  qsDesignEnableQsInstance?: boolean;
  qsemLpProviders?: ILPCoursesNumberProviders;
  qsemLearnerCustomRedirectUrl?: ICustomRedirectUrl;
  qsemDesignWhiteLabel?: boolean;
  qsemSsoSingleTenant?: boolean;
  qsemSsoSamlProviderId?: ISsoProviderId;
  qsemSsoGoogleDomains?: { domains?: string[] };
  uvaShowBottonIWouldLikeToDoAProject?: string[];
  qsemCustomSignupInfo?: {
    [key: string]: string;
    contactEmail: string;
  };
  qsemAutoAssignInstitutionByRegistrationCode?: boolean;
  qsemCoursePriceShowFilter?: boolean | IQsemCoursesFilterByPrice;
  qsemHideCourseCompleteButton?: boolean | IHideCompleteButtonConfig[];
  qsemShowSidebarButton?: IQsemShowSidebarButton;
  qsemLogoutImmediateRedirect?: IEmbededLogin;
  qsemDesignCustomLogin?: IQsemDesignCustomLogin;
  qsemContactInfoPlar?: IQsemContactInfoPlar;
  qsemShowProfileDropdown2FA?: boolean;
  qsemHomepageByStudentType?: IQsemHomepageByStudentType[];
  qsemMajorsEnableSpecializedMajors?: boolean;
  qsemDefaultAcademicProgram?: IDefaultAcademicProgram;
  qsemEnablePlar: boolean | IQsemPlarFeaturedPrograms;
  qsemShowChatBot?: IQsemShowChatBot;
  qsemVisualFeatures?: Partial<Record<VisualFeatureKey, boolean>>;
}

export interface IFeatureFlagsReducer {
  features: IFeatures;
  setFeatures: (initialValue: string[]) => void;
  addFeaturesWithValues?: (features: IFeatureWithValue[]) => void;
}

export interface IIFeatureFlagsReducerAction {
  type: string;
  initialValue: string[];
  features?: IFeatureWithValue[];
}

export const featuresFlagReducerState: IFeatureFlagsReducer = {
  features: {
    qsemLangDefaultLanguage: {
      language: 'en',
    },
    qsemEnableAuthSaml: environmentVariables?.qsemEnableAuthSaml === 'true',
    qsemLpProviders: {
      providers: [],
    },
    qsemLearnerCustomRedirectUrl: {
      url: '',
    },
    qsemSsoSingleTenant: false,
    qsemCustomSignupInfo: {
      en: '',
      contactEmail: '',
    },
    qsemLogoutImmediateRedirect: undefined,
    qsemHomepageByStudentType: undefined,
    qsemEnablePlar: false,
    qsemShowChatBot: {
      url: '',
      institutionName: '',
    },
  },
  setFeatures: () => null,
};

const flagsLearner = new Set([
  'QSEM_LEARNER_DEFAULT_LEVEL_OF_STUDY',
  'QSEM_LEARNER_DEFAULT_STUDENT_INSTITUTION',
  'QSEM_ACADEMIC_PROGRAM_LP_MAPPING',
]);

const featureFlagReducer = (state: IFeatureFlagsReducer, action: IIFeatureFlagsReducerAction): IFeatureFlagsReducer => {
  const defaultState = state || featuresFlagReducerState;

  const findFeature = (feature: string): boolean => action.initialValue.includes(feature);

  const hasFeature = (features: string[], feature: string): boolean => features.some((item) => item.includes(feature));

  switch (action.type) {
    case 'addFeatures':
      return {
        ...defaultState,
        features: {
          googleSSOEnabled: findFeature(FeaturesEnum.googleSSOEnabled),
          learner: {
            qsemEnablePlar: findFeature(FeaturesEnum.qsemEnablePlar),
            qsemLearnerMarketInsightsInterest: findFeature(FeaturesEnum.qsemLearnerMarketInsightsInterest),
          },
          occupationsLimitedEnabled: findFeature(FeaturesEnum.occupationsLimitedEnabled),
          qsemEnableAuthSaml: findFeature(FeaturesEnum.qsemEnableAuthSaml),
          qsDesignEnableQsInstance: findFeature(FeaturesEnum.qsDesignEnableQsInstance),
          qsemDesignWhiteLabel: findFeature(FeaturesEnum.qsemDesignWhiteLabel),
          qsemSsoSingleTenant: hasFeature(action.initialValue, FeaturesEnum.qsemSsoSingleTenant),
          qsemAutoAssignInstitutionByRegistrationCode: findFeature(
            FeaturesEnum.qsemAutoAssignInstitutionByRegistrationCode,
          ),
          qsemCoursePriceShowFilter: findFeature(FeaturesEnum.qsemCoursePriceShowFilter),
          qsemShowProfileDropdown2FA: findFeature(FeaturesEnum.qsemShowProfileDropdown2FA),
          qsemMajorsEnableSpecializedMajors: findFeature(FeaturesEnum.qsemMajorsEnableSpecializedMajors),
          qsemEnablePlar: findFeature(FeaturesEnum.qsemEnablePlar),
        },
      };

    case 'addFeaturesWithValues': {
      const featuresMapWithValues = action?.features?.reduce((map, feature) => {
        const normalizedKey = toCamelCaseFromSnakeCase(feature.name);

        const parsedValue = JSON.parse(feature.value);

        if (parsedValue === null) {
          return map;
        }

        const parsed =
          feature.name === FeaturesEnum.qsemVisualFeatures ? normalizeVisualFeatures(parsedValue) : parsedValue;

        if (flagsLearner.has(feature.name)) {
          return {
            ...map,
            learner: {
              ...map.learner,
              [normalizedKey]: parsed,
            },
          };
        }

        return {
          ...map,
          [normalizedKey]: parsed,
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }, {} as Record<string, any>);

      if (featuresMapWithValues) {
        return {
          ...defaultState,
          features: {
            ...defaultState.features,
            ...featuresMapWithValues,
            learner: {
              ...defaultState.features.learner,
              ...featuresMapWithValues.learner,
            },
          },
        };
      }

      return defaultState;
    }

    default:
      return state;
  }
};

export default featureFlagReducer;
